<script setup lang="ts">
import { onBeforeMount, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { toastApiResult } from "@/toaster";
import Switch from "@/ui/components/switch/switch.vue";

const { t } = useI18n();

const props = defineProps({
	availableNotificationTypes: {
		type: Array as PropType<App.Data.NotificationTypeDO[]>,
		required: true,
	},
	configuredNotificationTypes: {
		type: Array as PropType<App.Data.NotificationTypeDO[]>,
		required: true,
	},
	asyncUpdateFunction: {
		type: Function,
		required: true,
	},
});

const configuredNotificationTypes = ref(props.configuredNotificationTypes);
const openExpansionPanels = ref([0]);

onBeforeMount(() => {
	constructRelationsThatAreNotSaved();
});

const constructRelationsThatAreNotSaved = () => {
	props.availableNotificationTypes.forEach((item) => {
		if (
			!configuredNotificationTypes.value.some(
				(existingItem) => existingItem.id === item.id
			)
		) {
			item.pivot = {
				notification_type_id: item.id,
				channels: [],
			};
			configuredNotificationTypes.value.push(item);
		}
	});
};

const findConfiguredNotification = (
	name_clean: string
): App.Data.NotificationTypeDO => {
	return props.configuredNotificationTypes.find(
		(configuredNotificationType) =>
			configuredNotificationType.name_clean === name_clean
	);
};

const updateNotificationSettings = async (
	name_clean: string,
	channel: string
) => {
	const NotificationType = findConfiguredNotification(name_clean);
	const updateNotificationDTO: App.Data.UpdateNotificationDTO = {
		method: NotificationType.pivot.channels.includes(channel)
			? "subscribe"
			: "unsubscribe",
		channel,
		notification_type_id: NotificationType.id,
	};
	await props.asyncUpdateFunction(updateNotificationDTO);
};

// New function to handle channel toggle
const handleChannelToggle = (
	name_clean: string,
	channel: string,
	checked: boolean
) => {
	const notificationType = findConfiguredNotification(name_clean);
	if (checked) {
		if (!notificationType.pivot.channels.includes(channel)) {
			notificationType.pivot.channels.push(channel);
		}
	} else {
		notificationType.pivot.channels = notificationType.pivot.channels.filter(
			(c) => c !== channel
		);
	}
	updateNotificationSettings(name_clean, channel);
};

// Emit is not used in this component but required through the tabs-router-view setup.
defineEmits(["update-user"]);
</script>

<template>
	<v-expansion-panels v-model="openExpansionPanels">
		<v-expansion-panel
			v-for="availableNotificationType in availableNotificationTypes"
			:key="availableNotificationType.class"
			class="mt-5"
			elevation="2"
		>
			<v-expansion-panel-title>
				<span class="text-h6">
					Urgency:
					{{
						t(
							`notifications.types.${availableNotificationType.name_clean}.title`
						)
					}}
				</span>
				<v-spacer />
				<span class="text-small pr-6">
					{{ t("notifications.channelsSelected") }}
					{{
						findConfiguredNotification(availableNotificationType.name_clean)
							.pivot.channels.length
					}}
					/ {{ availableNotificationType.available_channels.length }}
				</span>
			</v-expansion-panel-title>
			<v-expansion-panel-text>
				<v-row class="pt-0 mt-0">
					<v-col cols="12">
						<div
							v-for="channel in availableNotificationType.available_channels"
							:key="availableNotificationType.class + channel"
						>
							<div class="d-flex align-sm-center align-start">
								<v-avatar
									size="48"
									class="mr-2"
									rounded="md"
									color="lightprimary"
								>
									<i
										v-if="channel === 'database'"
										class="fa-duotone fa-browser fa-lg text-primary"
									/>
									<i
										v-if="channel === 'mail'"
										class="fa-duotone fa-at fa-lg text-primary"
									/>
									<i
										v-if="channel === 'text'"
										class="fa-duotone fa-message-sms fa-lg text-primary"
									/>
									<i
										v-if="channel === 'whatsapp'"
										class="fa-duotone fa-circle-phone fa-lg text-primary"
									/>
								</v-avatar>
								<div class="pl-4">
									<h6 class="text-h6 mb-1 mt-1">
										{{ t(`notifications.channels.${channel}.title`) }}
									</h6>
									<h5 class="text-subtitle-1 text-medium-emphasis">
										{{ t(`notifications.channels.${channel}.description`) }}
									</h5>
								</div>
								<div class="ml-auto mr-1 pa-sm-1 pa-6 pl-7">
									<Switch
										:model-value="
											findConfiguredNotification(
												availableNotificationType.name_clean
											).pivot.channels.includes(channel)
										"
										@update:model-value="
											(checked) =>
												handleChannelToggle(
													availableNotificationType.name_clean,
													channel,
													checked
												)
										"
										hide-details
									/>
								</div>
							</div>
						</div>
					</v-col>
				</v-row>
			</v-expansion-panel-text>
		</v-expansion-panel>
	</v-expansion-panels>
</template>

<style lang="scss">
.v-expansion-panel-title__overlay {
	background: rgba(var(--v-theme-primary)) !important;
}
</style>
